import type { V2 } from '../../core/math';
import { Rng } from '../../core/rng';
import { DungeonBuilder } from '../../zones/DungeonBuilder';
import type { PropInstance, ZoneLayout } from '../../zones/types';

const RED = 0xd04040;
const BLUE = 0x4a6ad8;

/**
 * THE CRUCIBLE (3v3 PvP). A 50 × 50 walled arena, point-symmetric under a
 * 180° turn so neither team has an edge: red spawns on the south side
 * (`red_0..2`), blue on the north (`blue_0..2`, the mirror of red); `start`
 * is the centre. Line-of-sight breakers: four tall pillars around the centre,
 * two short walls east and west, and four low ruins near the spawns.
 */
export function buildCrucible(): ZoneLayout {
  const d = new DungeonBuilder(
    {
      id: 'crucible', name: 'The Crucible', subtitle: 'Three against three', kind: 'arena', biome: 'arena', levelRange: [20, 30], music: 'arena',
      width: 84, depth: 84, seed: 2901, ground: ['tiles', 'cobble', 'rock', 'dirt'], groundScale: [4, 4, 7, 5], ambient: 'none', mapTint: 0x30303a,
      wallHeight: 6, nearWallHeight: 1.0, accentAmount: 0.2, paint: { floor: 1, path: 0, wall: 2, accent: 3 },
      kit: {
        wall: { model: 'wall_segment', length: 8, depth: 1.2, scale: 0.75, tint: 0xe8e0d0 },
        post: { model: 'pillar_intact', every: 2, scale: [0.95, 1.05] },
        light: { model: 'brazier', every: 2 },
        low: [{ model: 'pillar_ruin', scale: [0.5, 0.6] }],
        rocks: [{ model: 'cliff_chunk', r: 2.9, scale: [0.5, 0.8] }],
        rockSpacing: 4,
      },
    },
    new Rng('crucible'),
  );
  d.hall(0, 0, 25, 25, { round: 6, path: true });

  // Point-symmetric cover: each piece is mirrored through the centre.
  const mirrored = (x: number, z: number, model: string, o: Omit<PropInstance, 'model' | 'x' | 'z'>): void => {
    d.prop(model, x, z, o);
    d.prop(model, -x, -z, { ...o, rot: (o.rot ?? 0) + Math.PI });
  };
  mirrored(8, 8, 'pillar_intact', { scale: 1.5, collider: { r: 0.6 } });
  mirrored(-8, 8, 'pillar_intact', { scale: 1.5, collider: { r: 0.6 } });
  mirrored(14, 0, 'wall_segment', { scale: 0.5, rot: Math.PI / 2, collider: { w: 8, d: 1.2 }, tint: 0xe8e0d0 });
  mirrored(11, 13.5, 'pillar_ruin', { scale: 0.9, collider: { r: 0.6 } });
  mirrored(-11, 13.5, 'pillar_ruin', { scale: 0.9, collider: { r: 0.6 } });

  const teams: [string, number, V2[]][] = [
    ['red', RED, [{ x: -5, z: 20.5 }, { x: 0, z: 21.5 }, { x: 5, z: 20.5 }]],
    ['blue', BLUE, [{ x: 5, z: -20.5 }, { x: 0, z: -21.5 }, { x: -5, z: -20.5 }]],
  ];
  for (const [team, tint, pts] of teams) {
    const facing = team === 'red' ? Math.PI : 0;
    pts.forEach((p, i) => d.spawn(`${team}_${i}`, p.x, p.z, facing));
    const zSide = team === 'red' ? 23 : -23;
    for (const x of [-9, 9]) d.prop('banner_pole', x, zSide, { collider: { r: 0.35 }, tint });
    d.paintCircle(0, zSide - Math.sign(zSide) * 2.5, 5, 0, 1);
  }
  d.spawn('start', 0, 0, Math.PI);
  d.paintCircle(0, 0, 4.5, 0, 0.6);
  d.paintRing(0, 0, 7.4, 8.2, 0, 0.3);
  d.prop('statue_hero', 0, -30.5, { scale: 0.85 });
  return d.build();
}
